import React, { useState } from 'react';
import styled from 'styled-components';
import { FileText, Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';
import DocumentViewerModal from '../../components/ui/DocumentViewerModal';
import { formatDate } from '../../utils/Utils';

// Adayın salt okunur profil bölümleri — TEK KAYNAK. İki yüzey çizer: hızlı bakış
// çekmecesi (CandidateDetailDrawer) ve aday detay sayfasının Genel sekmesi
// (/ats/candidates/:id). İki kopya ilk alan eklemesinde ayrışırdı.

export const SectionTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin: 22px 0 10px;
  &:first-child { margin-top: 0; }
`;

export const Rows = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: minmax(120px, 40%) 1fr;
  gap: 8px 16px;
`;

export const Label = styled.dt`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

export const Value = styled.dd`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  word-break: break-word;
`;

export const LongText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: pre-wrap;
  word-break: break-word;
  background: ${({ theme }) => theme.colors.bgColor};
  border-radius: 6px;
  padding: 12px 14px;
`;

const ResumeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: ${({ theme }) => theme.colors.bgColor};
  border-radius: 6px;
  padding: 10px 14px;
`;

const ResumeName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  word-break: break-all;
`;

const dash = (v) => (v === null || v === undefined || v === '' ? '-' : v);

export function resumeFileName(url) {
  if (!url) return '';
  try {
    const u = new URL(url);
    const parts = u.pathname.split('/').filter(Boolean);
    return decodeURIComponent(parts[parts.length - 1] || 'resume');
  } catch {
    return url.split('/').pop() || 'resume';
  }
}

export function candidateExpectedSalary(row, locale = 'tr') {
  if (!row || row.expectedSalary == null) return '-';
  const amount = Number(row.expectedSalary).toLocaleString(locale);
  return row.expectedSalaryCurrency ? `${amount} ${row.expectedSalaryCurrency}` : amount;
}

export default function CandidateProfileSections({ row }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const d = (v) => (v ? formatDate(v, lang) : '-');
  // Aday özgeçmişi ÖZEL sınıftadır (StoragePurposes.candidateResume): adres ne
  // bir <a href>'e ne bir <iframe>'e doğrudan verilebilir — DocumentViewerModal
  // `/v1/storage/view` üzerinden imzalı çözer (ApplicationResumeTab ile aynı desen).
  const [viewingResume, setViewingResume] = useState(false);
  if (!row) return null;

  return (
    <>
      <SectionTitle>{t('ats.candidate.contactSection')}</SectionTitle>
      <Rows>
        <Label>{t('ats.candidate.fullName')}</Label>
        <Value>{dash(row.fullName)}</Value>
        <Label>{t('ats.candidate.email')}</Label>
        <Value>{dash(row.email)}</Value>
        <Label>{t('ats.candidate.phone')}</Label>
        <Value>{dash(row.phone)}</Value>
        <Label>{t('ats.candidate.linkedInUrl')}</Label>
        <Value>{dash(row.linkedInUrl)}</Value>
        <Label>{t('ats.candidate.location')}</Label>
        <Value>{dash(row.location)}</Value>
      </Rows>

      <SectionTitle>{t('ats.candidate.professionSection')}</SectionTitle>
      <Rows>
        <Label>{t('ats.candidate.currentEmployer')}</Label>
        <Value>{dash(row.currentEmployer)}</Value>
        <Label>{t('ats.candidate.currentTitle')}</Label>
        <Value>{dash(row.currentTitle)}</Value>
        <Label>{t('ats.candidate.experienceYears')}</Label>
        <Value>{dash(row.experienceYears)}</Value>
        <Label>{t('ats.candidate.expectedSalary')}</Label>
        <Value>{candidateExpectedSalary(row, lang)}</Value>
        <Label>{t('ats.candidate.noticePeriodDays')}</Label>
        <Value>{dash(row.noticePeriodDays)}</Value>
      </Rows>

      <SectionTitle>{t('ats.candidate.sourceSection')}</SectionTitle>
      <Rows>
        <Label>{t('ats.candidate.sourceLabel')}</Label>
        <Value>{row.source ? t(`ats.candidate.source.${row.source}`, { defaultValue: row.source }) : '-'}</Value>
        <Label>{t('ats.candidate.sourceDetail')}</Label>
        <Value>{dash(row.sourceDetail)}</Value>
        <Label>{t('ats.candidate.isAlumni')}</Label>
        <Value>{row.isAlumni ? t('common.yes') : t('common.no')}</Value>
        <Label>{t('ats.candidate.applicationCount')}</Label>
        <Value>{row.applicationCount ?? 0}</Value>
        <Label>{t('ats.candidate.lastJobPosting')}</Label>
        <Value>{dash(row.lastJobPostingTitle)}</Value>
      </Rows>

      <SectionTitle>{t('ats.candidate.resume')}</SectionTitle>
      {row.resumeFileUrl ? (
        <ResumeRow>
          <ResumeName><FileText size={16} />{resumeFileName(row.resumeFileUrl)}</ResumeName>
          <Tooltip text={t('ats.candidate.viewResume')}>
            <IconButton
              data-testid="candidate-resume-view"
              aria-label={t('ats.candidate.viewResume')}
              onClick={() => setViewingResume(true)}
            >
              <Eye size={16} />
            </IconButton>
          </Tooltip>
        </ResumeRow>
      ) : (
        <LongText>{t('ats.candidate.noResume')}</LongText>
      )}

      {row.coverLetter && (
        <>
          <SectionTitle>{t('ats.candidate.coverLetter')}</SectionTitle>
          <LongText>{row.coverLetter}</LongText>
        </>
      )}

      <SectionTitle>{t('ats.candidate.timeline')}</SectionTitle>
      <Rows>
        <Label>{t('ats.candidate.createDate')}</Label>
        <Value>{d(row.createDate)}</Value>
        <Label>{t('ats.candidate.consentExpiresAt')}</Label>
        <Value>{d(row.consentExpiresAt)}</Value>
      </Rows>

      <DocumentViewerModal
        open={viewingResume}
        fileUrl={row.resumeFileUrl}
        title={t('ats.candidate.resume')}
        onClose={() => setViewingResume(false)}
      />
    </>
  );
}
